export const BASE_TIMELINE_HOURS = 24;
export const BASE_TIMELINE_MINUTES = BASE_TIMELINE_HOURS * 60;
export const MIN_TIMELINE_BAR_MINUTES = 30;

export const parseTimeToMinutes = (timeValue) => {
  if (!timeValue || typeof timeValue !== "string") {
    return 0;
  }

  const [hourString = "0", minuteString = "0"] = timeValue.split(":");
  const hours = Number.parseInt(hourString, 10);
  const minutes = Number.parseInt(minuteString, 10);

  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return 0;
  }

  return hours * 60 + minutes;
};

export const getTimelineRangeMinutes = (entry) => {
  const startMinutes = Math.max(
    0,
    Math.min(BASE_TIMELINE_MINUTES, parseTimeToMinutes(entry?.startTime)),
  );
  let finishMinutes = parseTimeToMinutes(entry?.finishTime);

  if (finishMinutes < startMinutes) {
    finishMinutes += BASE_TIMELINE_MINUTES;
  }

  return { startMinutes, finishMinutes };
};

export const getTimelineEndMinutes = (entries = []) => {
  const latestFinish = (Array.isArray(entries) ? entries : []).reduce(
    (latest, entry) =>
      Math.max(latest, getTimelineRangeMinutes(entry).finishMinutes),
    BASE_TIMELINE_MINUTES,
  );

  return Math.max(BASE_TIMELINE_MINUTES, Math.ceil(latestFinish / 60) * 60);
};

const formatClockHour = (hourIndex) =>
  String(hourIndex % BASE_TIMELINE_HOURS).padStart(2, "0");

export const formatTimelineHourLabel = (hourIndex) => formatClockHour(hourIndex);

export const getTimelineHourCount = (timelineEndMinutes) =>
  Math.max(
    BASE_TIMELINE_HOURS,
    Math.ceil((timelineEndMinutes || BASE_TIMELINE_MINUTES) / 60),
  );

export const buildTimelineHours = (timelineEndMinutes) =>
  Array.from(
    { length: getTimelineHourCount(timelineEndMinutes) },
    (_, hourIndex) => ({
      index: hourIndex,
      label: formatTimelineHourLabel(hourIndex),
    }),
  );

export const buildTimelineMarkers = (timelineEndMinutes, stepHours = 6) => {
  const endHour = getTimelineHourCount(timelineEndMinutes);
  const markers = [];

  for (let hourIndex = 0; hourIndex < endHour; hourIndex += stepHours) {
    markers.push(hourIndex);
  }

  if (markers[markers.length - 1] !== endHour) {
    markers.push(endHour);
  }

  return markers.map((hourIndex) => ({
    index: hourIndex,
    minutes: hourIndex * 60,
    label: formatTimelineHourLabel(hourIndex),
  }));
};

export const buildTimelineBarStyle = (
  entry,
  timelineEndMinutes = BASE_TIMELINE_MINUTES,
) => {
  const { startMinutes, finishMinutes } = getTimelineRangeMinutes(entry);
  const clampedFinish = Math.max(
    startMinutes + MIN_TIMELINE_BAR_MINUTES,
    Math.min(finishMinutes, timelineEndMinutes),
  );

  return {
    left: `${(startMinutes / timelineEndMinutes) * 100}%`,
    width: `${((clampedFinish - startMinutes) / timelineEndMinutes) * 100}%`,
  };
};
